import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { Payment } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const BillingWarningBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const loadBillingStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setShowPaymentWarning(false);
      setMessage(null);
      setRole(null);
      return;
    }
    try {
      const response = await apiService.get('/user/me');
      const data = response?.data ?? {};
      setShowPaymentWarning(Boolean(data.showPaymentWarning));
      setMessage(typeof data.message === 'string' ? data.message : null);
      setRole(typeof data.role === 'string' ? data.role : null);
    } catch {
      setShowPaymentWarning(false);
      setMessage(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadBillingStatus();
  }, [loadBillingStatus]);

  const handleUpdatePaymentMethod = async () => {
    try {
      setPortalLoading(true);
      setPortalError(null);
      const returnUrl = `${window.location.origin}/manage-account?tab=subscription`;
      const response = await apiService.createBillingPortalSession(returnUrl);
      if (response.error) {
        setPortalError(response.error);
      } else if (response.url) {
        window.location.href = response.url;
      } else {
        setPortalError('Failed to open billing portal');
      }
    } catch (err: any) {
      setPortalError(err.response?.data?.error || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  if (!showPaymentWarning) {
    return null;
  }

  const canUpdatePayment = role === 'ADMIN' || role === 'ADMIN_PM';
  const defaultMessage =
    'We could not process your subscription renewal. Your payment method may have expired or been declined. '
    + 'Please update your payment method to avoid service interruption.';

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        px: { xs: 1, sm: 2 },
        pt: 1,
      }}
    >
      <Alert
        severity="warning"
        sx={{ alignItems: 'flex-start' }}
        action={
          canUpdatePayment ? (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              startIcon={<Payment />}
              onClick={handleUpdatePaymentMethod}
              disabled={portalLoading}
              sx={{ whiteSpace: 'nowrap', mt: { xs: 1, sm: 0 } }}
            >
              {portalLoading ? 'Opening…' : 'Update Payment Method'}
            </Button>
          ) : undefined
        }
      >
        <AlertTitle>Payment Failed</AlertTitle>
        <Typography variant="body2" component="div">
          {message || defaultMessage}
        </Typography>
        {portalError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {portalError}
          </Typography>
        )}
      </Alert>
    </Box>
  );
};

export default BillingWarningBanner;
